/*
  NUDIENT: Galleriet och förstoringen på produktsidan, paket D9, M-089. Plan:
  nudient/PLAN-D9-galleri.md punkt 1 till 6. Laddas av
  snippets/nudient-tokens.liquid. Gör ingenting utan Nudients förstoring på
  sidan, blocks/nudient-gallery-lightbox.liquid.

  Galleriet på sidan är Horizons karusell, oförändrad. Skriptet:
  - lägger in räknaren "1 / 7" och zoomknappen i galleriet, 245:880 och
    357:4879, M-089 punkt 2. Räknaren följer Horizons händelse
    slideshow:select. Zoomknappen döljs när bilden är en video. Båda bär
    Horizons skyddsattribut, M-085.
  - öppnar förstoringen med zoomknappen eller en klick på bilden, 358:1080.
    Horizons karusell stoppar klicken efter ett drag, så ett svep öppnar
    ingenting.

  Förstoringen, GalleryLightbox 358:1080, M-089:
  - Webbläsarens egen dialog, showModal(), som sköter fokus och Escape, punkt
    1. Fokus börjar på X och går tillbaka till zoomknappen eller bilden som
    öppnade den, 1313:5653. Tab går runt i förstoringen och lämnar den inte,
    också förbi webbläsarens egna fält, som Horizons lådor.
  - Bilderna läses ur Horizons galleri, i samma ordning, bara bilder, punkt 9.
  - En rad med en bild per skärm. Svep och styrplatta byter bild, pilarna och
    piltangenterna också, och vid första och sista bilden döljs pilen och
    tangenten gör ingenting, punkt 8.
  - Zoomed är 1,6 gånger hela bilden från 768 och 1,8 gånger i mobil, men
    aldrig bredare än bildens egen bredd, M-089 punkt 5 och tillägget. Fit är
    hela bilden, så stor som scenen rymmer med 16 i sidorna, 358:1038.
  - Klick på bilden växlar, och punkten under muspekaren står kvar. I mobil
    dubbeltryck, punkt 6. I Zoomed rullas bilden åt alla håll och börjar mitt
    i. Skift och piltangenterna rullar bilden.
  - Tillbaka-knappen i mobil stänger förstoringen och stannar på produkten,
    punkt 7, med en egen post i webbläsarens historik.
  - Bilderna laddas först när förstoringen öppnas: den som visas och dess
    grannar. Först visas galleriets bild, som redan är laddad, och sedan en
    större i den bredd som behövs, plan punkt 6.
  - När förstoringen stängs visar galleriet samma bild, som Horizon.

  Värden som inte är tokens:
  - 768: M-017, samma gräns som CSS:ens mediafrågor, (min-width: 768px).
  - 1.6 och 1.8: zoomnivåerna, 358:1009 och 358:1058, M-089 punkt 5.
  - 16: luften i sidorna för Fit, 358:1038, 358 bred i 390.
  - 240 till 3840: Horizons bildbredder, snippets/product-media.liquid rad 20.
  - 300 och 30: dubbeltryckets tid i millisekunder och största avstånd i px,
    som Horizons assets/drag-zoom-wrapper.js rad 8 och 9 i närheten, 300 och 50.
    30 är valt mindre, så att ett svep aldrig räknas som tryck.
  - 10: rörelse i px som gör ett tryck till ett drag, som Horizon, rad 10.
  - 80: hur långt Skift och pil rullar bilden, i px, inte ritat.
  - 700: millisekunder som en mjuk rullning till nästa bild får ta innan
    snäppningen slås på igen. none i scroll-snap-type under tiden, eftersom
    Safari annars snäpper tillbaka, provat 12 oktober 2026.
  - 15: över 15 bilder visas inga streck, bara räknaren, som Horizon gör med
    prickarna, M-089 punkt 9.
  - nudientLightbox: nyckeln för historikens post.
  - 0.8: förhållandet 4:5, ritningens 1000 × 1250 i 358:1011, som används om
    en bilds mått saknas.
*/

import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const SCOPE = '.product-information:has(.nudient-product-header)';
const WIDE = window.matchMedia('(min-width: 768px)');
const ZOOM_WIDE = 1.6;
const ZOOM_NARROW = 1.8;
const SIDE = 16;
const WIDTHS = [240, 352, 640, 832, 1200, 1600, 1920, 2560, 3840];
const MAX_LINES = 15;

/** Bildens adress i en viss bredd, ur Shopifys adress */
function sized(src, width) {
  const url = new URL(src, location.href);
  url.searchParams.delete('height');
  url.searchParams.set('width', String(width));
  return url.toString();
}

/** Minsta av Horizons bredder som täcker behovet, aldrig över originalet */
function pickWidth(needed, natural) {
  const width = WIDTHS.find((w) => w >= needed) ?? WIDTHS[WIDTHS.length - 1];
  return natural ? Math.min(width, natural) : width;
}

class NudientGalleryLightbox extends HTMLElement {
  /** @type {HTMLDialogElement} */ dialog;
  /** @type {HTMLElement | null} */ gallery = null;
  /** @type {Array<{slide: number, src: string, width: number, height: number, alt: string, thumb: string}>} */
  images = [];
  index = 0;
  zoomed = true;
  /** @type {HTMLElement | null} */ trigger = null;
  pushed = false;
  active = false;
  scrollTop = 0;
  /** @type {number | undefined} */ #settle;
  #abort = new AbortController();

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.gallery = this.closest(SCOPE)?.querySelector('media-gallery') ?? null;
    if (!this.dialog || !this.gallery) return;

    const { signal } = this.#abort;
    this.#insertControls();
    this.gallery.setAttribute('data-nudient-lightbox', '');

    this.gallery.addEventListener('click', this.#onGalleryClick, { signal });
    this.gallery.addEventListener('slideshow:select', this.#onGallerySelect, { signal });
    this.dialog.addEventListener('close', this.#onClose, { signal });
    this.dialog.addEventListener('keydown', this.#onKeyDown, { signal });
    this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.close(), { signal });
    this.dialog.querySelector('[data-previous]')?.addEventListener('click', () => this.go(this.index - 1), { signal });
    this.dialog.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1), { signal });
    this.track.addEventListener('scroll', this.#onTrackScroll, { signal, passive: true });
    window.addEventListener('popstate', this.#onPopState, { signal });
    window.addEventListener('resize', () => this.dialog.open && this.#layout(true), { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.gallery?.querySelectorAll('.nudient-gallery-counter, .nudient-gallery-zoom').forEach((el) => el.remove());
    this.gallery?.removeAttribute('data-nudient-lightbox');
  }

  get track() {
    return /** @type {HTMLElement} */ (this.dialog.querySelector('[data-track]'));
  }

  get slideshow() {
    return /** @type {any} */ (this.gallery?.querySelector('slideshow-component'));
  }

  /* ---------------------------------------------------------------------- */
  /* Galleriet på sidan                                                     */
  /* ---------------------------------------------------------------------- */

  #insertControls() {
    const container = this.gallery?.querySelector('slideshow-container');
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-gallery-controls]'));
    if (!container || !template || container.querySelector('.nudient-gallery-zoom')) return;

    container.append(template.content.cloneNode(true));
    container.querySelector('.nudient-gallery-zoom')?.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        this.open(this.slideshow?.current ?? 0, /** @type {HTMLElement} */ (event.currentTarget));
      },
      { signal: this.#abort.signal }
    );
    this.#updateGalleryControls(this.slideshow?.current ?? 0);
  }

  /** @param {number} slideIndex */
  #updateGalleryControls(slideIndex) {
    const slides = this.#gallerySlides();
    const counter = this.gallery?.querySelector('.nudient-gallery-counter');
    const zoom = /** @type {HTMLElement | null} */ (this.gallery?.querySelector('.nudient-gallery-zoom'));
    if (counter) {
      counter.textContent = `${slideIndex + 1} / ${slides.length}`;
      counter.toggleAttribute('hidden', slides.length < 2);
    }
    if (zoom) zoom.hidden = !this.#isImageSlide(slides[slideIndex]);
  }

  /** Karusellens egna bilder, Horizons lista. Inte querySelectorAll, eftersom en
      video tillfälligt lägger till ett element när den väljs. */
  #gallerySlides() {
    const slides = this.slideshow?.refs?.slides;
    if (Array.isArray(slides) && slides.length) return /** @type {HTMLElement[]} */ (slides);
    return /** @type {HTMLElement[]} */ ([...(this.slideshow?.querySelectorAll(':scope slideshow-slide') ?? [])]);
  }

  /** @param {Element | undefined} slide */
  #isImageSlide(slide) {
    return Boolean(slide?.classList.contains('product-media-container--image') && slide.querySelector('img'));
  }

  #onGallerySelect = (/** @type {any} */ event) => {
    if (typeof event.detail?.index === 'number') this.#updateGalleryControls(event.detail.index);
  };

  #onGalleryClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {Element} */ (event.target);
    if (target.closest('slideshow-controls, slideshow-arrows, button, a')) return;
    const slide = /** @type {HTMLElement | null} */ (target.closest('slideshow-slide'));
    if (!slide || !this.#isImageSlide(slide)) return;
    const slideIndex = this.#gallerySlides().indexOf(slide);
    if (!slide.hasAttribute('tabindex')) slide.setAttribute('tabindex', '-1');
    this.open(slideIndex, slide);
  };

  /* ---------------------------------------------------------------------- */
  /* Förstoringen                                                           */
  /* ---------------------------------------------------------------------- */

  /** Läser bilderna ur galleriet, i galleriets ordning, bara bilder */
  #readImages() {
    this.images = [];
    /** @type {Record<string, [number, number]>} */
    let sizes = {};
    try {
      sizes = JSON.parse(this.querySelector('script[data-media-sizes]')?.textContent || '{}');
    } catch {}
    const slides = this.#gallerySlides();
    const thumbs = [...(this.gallery?.querySelectorAll('slideshow-controls[thumbnails] .slideshow-controls__thumbnail img') ?? [])];
    slides.forEach((slide, slideIndex) => {
      if (!this.#isImageSlide(slide)) return;
      const img = /** @type {HTMLImageElement} */ (slide.querySelector('img'));
      const src = img.currentSrc || img.getAttribute('src') || '';
      const thumb = /** @type {HTMLImageElement | undefined} */ (thumbs[slideIndex]);
      const size = sizes[slide.querySelector('[data-media-id]')?.getAttribute('data-media-id') || ''] || [0, 0];
      this.images.push({
        slide: slideIndex,
        src,
        width: size[0],
        height: size[1],
        alt: img.getAttribute('alt') || '',
        thumb: thumb?.getAttribute('src') || src,
      });
    });
  }

  #build() {
    const status = this.dataset.labelStatus || '';
    const showLabel = this.dataset.labelShow || '';
    const thumbsOn = this.dataset.thumbnails === 'true';

    this.track.replaceChildren(
      ...this.images.map((image, i) => {
        const slide = document.createElement('div');
        slide.className = 'nudient-lightbox__slide';
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', this.dataset.labelImage || '');
        slide.setAttribute('aria-label', status.replace('[index]', `${i + 1}`).replace('[count]', `${this.images.length}`));
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'nudient-lightbox__image';
        const img = document.createElement('img');
        img.alt = image.alt;
        img.decoding = 'async';
        img.draggable = false;
        button.append(img);
        slide.append(button);
        this.#bindImage(button, i);
        return slide;
      })
    );

    const thumbs = /** @type {HTMLElement} */ (this.dialog.querySelector('[data-thumbs]'));
    thumbs.hidden = !thumbsOn;
    thumbs.replaceChildren(
      ...(thumbsOn
        ? this.images.map((image, i) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'nudient-lightbox__thumb';
            button.setAttribute('aria-label', showLabel.replace('[index]', `${i + 1}`));
            const img = document.createElement('img');
            img.alt = '';
            img.loading = 'lazy';
            img.src = image.thumb;
            button.append(img);
            button.addEventListener('click', () => this.go(i));
            return button;
          })
        : [])
    );
    this.dialog.toggleAttribute('data-no-thumbs', !thumbsOn);

    const progress = /** @type {HTMLElement} */ (this.dialog.querySelector('[data-progress]'));
    progress.hidden = this.images.length < 2 || this.images.length > MAX_LINES;
    progress.replaceChildren(
      ...this.images.map(() => {
        const line = document.createElement('span');
        line.className = 'nudient-lightbox__line';
        return line;
      })
    );
  }

  /**
   * @param {number} slideIndex - galleriets bild
   * @param {HTMLElement | null} trigger - det som öppnade, får fokus tillbaka
   */
  open(slideIndex, trigger) {
    if (this.dialog.open) return;
    this.#readImages();
    if (!this.images.length) return;
    this.#build();
    this.trigger = trigger;
    this.zoomed = this.dataset.openState !== 'fit';
    const index = Math.max(0, this.images.findIndex((image) => image.slide === slideIndex));

    this.scrollTop = getScrollTop();
    lockScroll(this.dialog);
    this.dialog.showModal();
    this.active = true;
    history.pushState({ nudientLightbox: true }, '');
    this.pushed = true;
    this.#layout(false);
    this.go(index, false);
  }

  /** Stänger med X. Escape stänger dialogen direkt, webbläsarens eget. */
  close() {
    if (this.dialog.open) this.dialog.close();
    this.#onClose();
  }

  /** Tillbaka-knappen: historikens post är redan borta, så stäng bara */
  #onPopState = () => {
    if (!this.active) return;
    this.pushed = false;
    if (this.dialog.open) this.dialog.close();
    this.#onClose();
  };

  /** Dialogen har stängts, på vilket sätt som helst. Körs en gång per öppning. */
  #onClose = () => {
    if (!this.active) return;
    this.active = false;
    if (this.pushed) {
      this.pushed = false;
      if (history.state?.nudientLightbox) history.back();
    }
    const image = this.images[this.index];
    unlockScroll(this.dialog);
    scrollTo({ top: this.scrollTop, behavior: 'instant' });
    if (image && this.slideshow?.current !== image.slide) {
      this.slideshow?.select(image.slide, undefined, { animate: false });
    }
    this.trigger?.focus({ preventScroll: true });
    this.trigger = null;
    delete this.dialog.dataset.ready;
  };

  /**
   * @param {number} index
   * @param {boolean} [smooth]
   */
  go(index, smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    if (index < 0 || index >= this.images.length) return;
    const slide = /** @type {HTMLElement} */ (this.track.children[index]);
    // Snäppningen av medan skriptet byter bild. Safari snäpper annars tillbaka
    // mitt i en mjuk rullning, provat 12 oktober 2026.
    const track = this.track;
    track.style.scrollSnapType = 'none';
    track.dataset.moving = 'true';
    track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'instant' });
    clearTimeout(this.#settle);
    this.#settle = window.setTimeout(() => {
      track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
      track.style.removeProperty('scroll-snap-type');
      delete track.dataset.moving;
    }, smooth ? 700 : 0);
    this.#setIndex(index);
  }

  /** @param {number} index */
  #setIndex(index) {
    const changed = index !== this.index || !this.dialog.dataset.ready;
    this.index = index;
    this.dialog.dataset.ready = 'true';
    const count = this.images.length;
    const counter = this.dialog.querySelector('[data-counter]');
    const live = this.dialog.querySelector('[data-counter-live]');
    if (counter) counter.textContent = `${index + 1} / ${count}`;
    if (live && changed) {
      live.textContent = (this.dataset.labelStatus || '').replace('[index]', `${index + 1}`).replace('[count]', `${count}`);
    }
    /** @type {HTMLButtonElement | null} */ (this.dialog.querySelector('[data-previous]'))?.toggleAttribute('hidden', index === 0);
    /** @type {HTMLButtonElement | null} */ (this.dialog.querySelector('[data-next]'))?.toggleAttribute('hidden', index === count - 1);
    this.dialog.querySelectorAll('.nudient-lightbox__thumb').forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      if (i === index) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    this.dialog.querySelectorAll('.nudient-lightbox__line').forEach((line, i) => line.toggleAttribute('data-current', i === index));
    for (const i of [index, index - 1, index + 1]) this.#load(i);
  }

  #onTrackScroll = () => {
    if (this.track.dataset.moving) return;
    const width = this.track.clientWidth;
    if (!width) return;
    const index = Math.round(this.track.scrollLeft / width);
    if (index !== this.index && index >= 0 && index < this.images.length) this.#setIndex(index);
  };

  /* ---------------------------------------------------------------------- */
  /* Storlek, Zoomed och Fit                                                */
  /* ---------------------------------------------------------------------- */

  get factor() {
    return WIDE.matches ? ZOOM_WIDE : ZOOM_NARROW;
  }

  /** Storleken för en bild i Fit och Zoomed */
  /** @param {number} i */
  #size(i) {
    const image = this.images[i];
    const slide = /** @type {HTMLElement} */ (this.track.children[i]);
    const ratio = image.width && image.height ? image.width / image.height : 0.8;
    const fitWidth = Math.min(slide.clientWidth - SIDE * 2, slide.clientHeight * ratio);
    const zoomWidth = Math.max(fitWidth, Math.min(fitWidth * this.factor, image.width || Infinity));
    const width = this.zoomed ? zoomWidth : fitWidth;
    return { width, height: width / ratio, zoomWidth, fitWidth };
  }

  /**
   * Sätter storleken på alla bilder och mittställer dem.
   * @param {boolean} keepIndex - behåll vald bild efter storleksändring
   */
  #layout(keepIndex) {
    this.images.forEach((_, i) => this.#applySize(i, null));
    if (keepIndex) this.go(this.index, false);
  }

  /**
   * @param {number} i
   * @param {{x: number, y: number} | null} focus - punkten som ska stå kvar, i bildens bråkdelar
   * @param {{x: number, y: number}} [screen] - var punkten står på skärmen, i scenen
   */
  #applySize(i, focus, screen) {
    const slide = /** @type {HTMLElement} */ (this.track.children[i]);
    const button = /** @type {HTMLElement} */ (slide?.firstElementChild);
    if (!button) return;
    const { width, height, zoomWidth, fitWidth } = this.#size(i);
    button.style.width = `${width}px`;
    button.style.height = `${height}px`;
    const canZoom = zoomWidth > fitWidth;
    button.setAttribute(
      'aria-label',
      (this.zoomed ? this.dataset.labelZoomOut : this.dataset.labelZoomIn) || ''
    );
    button.dataset.state = this.zoomed ? 'zoomed' : 'fit';
    button.toggleAttribute('data-static', !canZoom);

    const fx = focus?.x ?? 0.5;
    const fy = focus?.y ?? 0.5;
    const sx = screen?.x ?? slide.clientWidth / 2;
    const sy = screen?.y ?? slide.clientHeight / 2;
    const left = button.offsetLeft + fx * width - sx;
    const top = button.offsetTop + fy * height - sy;
    slide.scrollTo({ left, top, behavior: 'instant' });
  }

  /** @param {boolean} zoomed */
  /** @param {{x: number, y: number} | null} [focus] */
  /** @param {{x: number, y: number}} [screen] */
  #setZoom(zoomed, focus = null, screen) {
    if (zoomed === this.zoomed) return;
    this.zoomed = zoomed;
    this.images.forEach((_, i) => this.#applySize(i, i === this.index ? focus : null, i === this.index ? screen : undefined));
    for (const i of [this.index, this.index - 1, this.index + 1]) this.#load(i);
  }

  /**
   * Klick växlar, i mobil dubbeltryck.
   * @param {HTMLElement} button
   * @param {number} i
   */
  #bindImage(button, i) {
    let pointerType = 'mouse';
    let downX = 0;
    let downY = 0;
    let lastTap = 0;
    let lastX = 0;
    let lastY = 0;
    let moved = false;

    button.addEventListener('pointerdown', (event) => {
      pointerType = event.pointerType;
      downX = event.clientX;
      downY = event.clientY;
      moved = false;
    });
    button.addEventListener('pointermove', (event) => {
      if (Math.hypot(event.clientX - downX, event.clientY - downY) > 10) moved = true;
    });
    button.addEventListener('click', (event) => {
      // Tangentbordet: ingen punkt, bilden mittställs
      const fromKeyboard = event.detail === 0;
      if (moved && !fromKeyboard) return;
      if (pointerType === 'touch' && !fromKeyboard) {
        const now = event.timeStamp;
        const double = now - lastTap < 300 && Math.hypot(event.clientX - lastX, event.clientY - lastY) < 30;
        lastTap = double ? 0 : now;
        lastX = event.clientX;
        lastY = event.clientY;
        if (!double) return;
      }
      const rect = button.getBoundingClientRect();
      const slideRect = /** @type {HTMLElement} */ (button.parentElement).getBoundingClientRect();
      const focus = fromKeyboard ? null : { x: (event.clientX - rect.left) / rect.width, y: (event.clientY - rect.top) / rect.height };
      const screen = fromKeyboard ? undefined : { x: event.clientX - slideRect.left, y: event.clientY - slideRect.top };
      if (i !== this.index || button.hasAttribute('data-static')) return;
      this.#setZoom(!this.zoomed, focus, screen);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Laddning                                                               */
  /* ---------------------------------------------------------------------- */

  /** @param {number} i */
  #load(i) {
    const image = this.images[i];
    const slide = this.track.children[i];
    const img = /** @type {HTMLImageElement | null} */ (slide?.querySelector('img'));
    if (!image || !img) return;
    const { zoomWidth } = this.#size(i);
    const width = pickWidth(Math.ceil(zoomWidth * (window.devicePixelRatio || 1)), image.width);
    if (Number(img.dataset.width || 0) >= width) return;
    if (!img.getAttribute('src')) img.src = image.src;
    img.dataset.width = String(width);
    const big = new Image();
    big.src = sized(image.src, width);
    big.decode().then(
      () => {
        if (Number(img.dataset.width) === width) img.src = big.src;
      },
      () => {}
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Tangentbordet                                                          */
  /* ---------------------------------------------------------------------- */

  /** Tab stannar i förstoringen: från sista till första och tvärtom */
  /** @param {KeyboardEvent} event */
  #trapTab(event) {
    const focusable = /** @type {HTMLElement[]} */ (
      [...this.dialog.querySelectorAll('button:not([hidden])')].filter((el) => el.getClientRects().length > 0)
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !this.dialog.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  #onKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Tab') {
      this.#trapTab(event);
      return;
    }
    const keys = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: 0, ArrowDown: 0 };
    if (!(event.key in keys) || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.shiftKey) {
      const slide = /** @type {HTMLElement} */ (this.track.children[this.index]);
      const step = 80;
      const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
      const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
      slide?.scrollBy({ left: dx, top: dy, behavior: 'instant' });
      event.preventDefault();
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') return;
    event.preventDefault();
    this.go(this.index + keys[/** @type {'ArrowLeft' | 'ArrowRight'} */ (event.key)]);
  };
}

if (!customElements.get('nudient-gallery-lightbox')) {
  customElements.define('nudient-gallery-lightbox', NudientGalleryLightbox);
}
